import { useEffect, useRef, useState } from "react";
import type { LoadState } from "./types";

// How long to keep showing the previous payload (or the initial blank
// state) before falling back to a visible "Loading…" indicator. Fast
// responses (<300ms) never flash loading at all; slower ones get the
// usual spinner message. Tuned to the median response time of the
// local gateway so the common case stays jitter-free without hiding
// genuine slowness.
const LOADING_FLASH_MS = 300;

// useFetch wraps a URL in a LoadState sum type. Abort controller
// cancels the in-flight request on unmount or url change so a quick
// nav does not race older responses into state.
//
// Loading-flash tuning: on URL change we keep showing the previous
// "ok" / "error" state for LOADING_FLASH_MS; only if the new fetch
// is still pending after that window do we flip to the loading
// state. Initial mount (no prior data) still shows loading
// immediately so the user doesn't stare at a blank page.
export function useFetch<T>(url: string): LoadState<T> {
  const [state, setState] = useState<LoadState<T>>({ kind: "loading" });
  // Track whether we have any resolved payload so the first mount
  // falls through to the "show loading immediately" branch.
  const hasSettledRef = useRef(false);

  useEffect(() => {
    // Empty URL = caller opted out of this fetch (e.g. TeamPage
    // skipping /v1/standings for sports that return 400 on it). Hold
    // the state as loading so the UI's null-standing branches render
    // without surfacing a fetch error.
    if (url === "") {
      return;
    }

    const ctrl = new AbortController();
    let settled = false;

    // If we've already shown real data at least once, keep it on
    // screen for LOADING_FLASH_MS before swapping in the loading
    // indicator. If the fetch returns inside that window the user
    // never sees a loading flash.
    let flashTimer: number | undefined;
    if (hasSettledRef.current) {
      flashTimer = window.setTimeout(() => {
        if (!settled) setState({ kind: "loading" });
      }, LOADING_FLASH_MS);
    } else {
      setState({ kind: "loading" });
    }

    fetch(url, { signal: ctrl.signal })
      .then(async (resp) => {
        if (!resp.ok) {
          throw new Error(`${resp.status} ${resp.statusText}`);
        }
        return resp.json() as Promise<T>;
      })
      .then((data) => {
        settled = true;
        if (flashTimer !== undefined) window.clearTimeout(flashTimer);
        hasSettledRef.current = true;
        setState({ kind: "ok", data });
      })
      .catch((err: unknown) => {
        settled = true;
        if (flashTimer !== undefined) window.clearTimeout(flashTimer);
        if (err instanceof Error && err.name === "AbortError") return;
        hasSettledRef.current = true;
        setState({
          kind: "error",
          message: err instanceof Error ? err.message : String(err),
        });
      });

    return () => {
      if (flashTimer !== undefined) window.clearTimeout(flashTimer);
      ctrl.abort();
    };
  }, [url]);

  return state;
}
